import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, Container, Stack, Typography } from '@mui/material';
import { tokenStore } from '../../shared/services/api-client';
import { useAuth } from '../../shared/hooks/useAuth';
import { usersService } from '../../shared/services/users.service';
import { useNotification } from '../../shared/notifications/NotificationProvider';
import { useApiError } from '../../shared/errors/useApiError';
import type { AuthUser, Role } from '../../shared/types';

/**
 * Landing route hit by the backend after a successful Google OAuth flow.
 *
 * URL contract (set by {@code OAuthService.handleCallback}):
 *   /auth/callback?token=...&refresh=...&next=...
 *
 * Responsibilities:
 *   1. Pull tokens out of the query string and stuff them into tokenStore so
 *      every subsequent API call attaches the access token.
 *   2. Strip the tokens from the URL immediately so they don't leak into
 *      browser history, the Referer header, or analytics tools.
 *   3. Fetch /users/me to hydrate the AuthProvider's user object (the OAuth
 *      callback doesn't return one — it just returns tokens).
 *   4. Navigate to {@code next} (or /dashboard) once the session is live.
 *
 * On any failure (missing tokens, /users/me 401, etc.) bounce back to /login
 * with a friendly notice so the user can try a different method.
 */
export function OAuthCallbackPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { setSession } = useAuth();
  const { notify } = useNotification();
  const { translate } = useApiError();
  const [error, setError] = useState<string | null>(null);
  // StrictMode mounts effects twice in dev — guard so we only consume the
  // one-shot tokens once.
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const accessToken = params.get('token');
    const refreshToken = params.get('refresh');
    const next = params.get('next') ?? '/dashboard';

    if (!accessToken || !refreshToken) {
      notify.error(t('auth.oauthFailed'));
      navigate('/login', { replace: true });
      return;
    }

    // 1. Stash tokens so /users/me carries the Authorization header.
    tokenStore.setAccess(accessToken);
    tokenStore.setRefresh(refreshToken);

    // 2. Scrub query string from history immediately.
    if (typeof window !== 'undefined') {
      window.history.replaceState(null, '', window.location.pathname);
    }

    // 3. Resolve the user, then setSession + navigate.
    usersService
      .getMe()
      .then((me) => {
        const user: AuthUser = {
          id: me.id,
          email: me.email,
          firstName: me.firstName ?? null,
          lastName: me.lastName ?? null,
          phoneNumber: me.phoneNumber ?? null,
          emailVerified: me.emailVerified,
          phoneVerified: me.phoneVerified,
          roles: (me.roles ?? []) as Role[],
          permissions: me.permissions ?? [],
        };
        setSession({ accessToken, refreshToken, user });
        notify.success(t('auth.loginSuccess'));
        const safeNext = next.startsWith('/') ? next : '/dashboard';
        navigate(safeNext, { replace: true });
      })
      .catch((err) => {
        // Clear the partial session so the user isn't stuck "half-logged-in".
        tokenStore.clearAll();
        const msg = translate(err);
        setError(msg);
        notify.error(msg);
        navigate('/login', { replace: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container maxWidth="sm" sx={{ py: 12, textAlign: 'center' }}>
      <Stack spacing={3} alignItems="center">
        <Box>
          {!error && <CircularProgress />}
        </Box>
        <Typography variant="h6">
          {error ? t('auth.oauthFailed') : t('auth.oauthSigningIn')}
        </Typography>
      </Stack>
    </Container>
  );
}
